import React, { useEffect, useMemo, useState } from 'react';

export type SiteAdsConfig = {
  enabled?: boolean;
  /** Google AdSense publisher id, e.g. ca-pub-123... */
  adsenseClient?: string;
  /** Optional slot ids */
  adsenseSlotDetail?: string;
  adsenseSlotList?: string;
  /** 百度联盟 / 百青藤 联盟 ID 或脚本片段标识（有值且 enabled 才渲染容器） */
  baiduUnionId?: string;
};

declare global {
  interface Window {
    __SITE_ADS__?: SiteAdsConfig;
    adsbygoogle?: unknown[];
  }
}

export function readSiteAds(): Required<Pick<SiteAdsConfig, 'enabled'>> & SiteAdsConfig {
  const cfg = window.__SITE_ADS__ || {};
  return {
    enabled: Boolean(cfg.enabled),
    adsenseClient: (cfg.adsenseClient || '').trim(),
    adsenseSlotDetail: (cfg.adsenseSlotDetail || '').trim(),
    adsenseSlotList: (cfg.adsenseSlotList || '').trim(),
    baiduUnionId: (cfg.baiduUnionId || '').trim(),
  };
}

/** 审核通过前保持 enabled=false，避免空广告框伤体验/收录 */
export const AdsBootstrap: React.FC = () => {
  useEffect(() => {
    const cfg = readSiteAds();
    if (!cfg.enabled || !cfg.adsenseClient) return;
    if (document.querySelector('script[data-ads="adsense"]')) return;
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(cfg.adsenseClient)}`;
    s.crossOrigin = 'anonymous';
    s.setAttribute('data-ads', 'adsense');
    document.head.appendChild(s);
  }, []);
  return null;
};

type AdSlotProps = {
  /** detail = 工具详情底部；list = 列表页底部 */
  placement: 'detail' | 'list';
  style?: React.CSSProperties;
};

/**
 * 广告位：未开启或未配置时渲染 null（不占版面）。
 * AdSense 通过后在 index.html 填 client/slot 并 enabled:true。
 */
export const AdSlot: React.FC<AdSlotProps> = ({ placement, style }) => {
  const cfg = useMemo(() => readSiteAds(), []);
  const [ready, setReady] = useState(false);

  const slotId = placement === 'detail' ? cfg.adsenseSlotDetail : cfg.adsenseSlotList;
  const canShowAdsense = cfg.enabled && Boolean(cfg.adsenseClient) && Boolean(slotId);
  const canShowBaidu = cfg.enabled && Boolean(cfg.baiduUnionId);

  useEffect(() => {
    if (!canShowAdsense) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      setReady(true);
    } catch {
      setReady(false);
    }
  }, [canShowAdsense, slotId]);

  if (!canShowAdsense && !canShowBaidu) return null;

  return (
    <aside
      aria-label="Sponsored"
      style={{
        margin: '24px 0',
        padding: '12px 0',
        borderTop: '1px solid #eef2f6',
        borderBottom: '1px solid #eef2f6',
        minHeight: canShowAdsense ? 90 : undefined,
        ...style,
      }}
    >
      {canShowAdsense && (
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-client={cfg.adsenseClient}
          data-ad-slot={slotId}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      )}
      {canShowBaidu && (
        <div
          id={`baidu-union-${placement}`}
          data-baidu-union={cfg.baiduUnionId}
          style={{ minHeight: 60 }}
        />
      )}
      {!ready && canShowAdsense && (
        <div style={{ fontSize: 12, color: '#94a3b8' }}>Ad</div>
      )}
    </aside>
  );
};

export default AdsBootstrap;
